import type { Renderer } from '../lib/connector';

type ZoneType = 'upnp' | 'airplay' | 'chromecast' | 'browser' | 'manual' | 'lua' | 'psaudio' | 'local-native';

const LOCAL_ZONE_IDS = new Set(['local_computer', 'this_computer', 'this_device', 'local']);

export function isSystemOutputLabel(name: string | null | undefined): boolean {
  const n = String(name || '').trim().toLowerCase();
  return n === 'system output' || n === 'system audio' || n === 'player.system_output';
}

export function isLocalSystemOutputZone(zone: {
  id?: string;
  type?: string;
  name?: string;
} | null | undefined): boolean {
  if (!zone) return true;
  const id = String(zone.id || '').toLowerCase();
  if (LOCAL_ZONE_IDS.has(id)) return true;
  if (zone.type === 'browser' || zone.type === 'local') return true;
  return isSystemOutputLabel(zone.name);
}

function isPsAudioRenderer(r: Renderer): boolean {
  if (isLocalSystemOutputZone({ id: r.id, name: r.name, type: r.transport_type })) return false;
  if (r.is_psaudio === true) return true;
  const manufacturer = String(r.manufacturer || '').toLowerCase();
  return manufacturer.includes('ps audio') || manufacturer.includes('psaudio');
}

export function classifyRendererZoneType(r: Renderer): ZoneType {
  if (isLocalSystemOutputZone({ id: r.id, name: r.name, type: r.transport_type })) {
    return r.transport_type === 'local-native' ? 'local-native' : 'browser';
  }
  const transport = (r.transport_type || 'upnp').toLowerCase();
  if (transport === 'local-native') return 'local-native';
  if (transport === 'bluesound') return 'manual';
  if (transport === 'raat' || transport === 'lua') return 'psaudio';
  if (isPsAudioRenderer(r)) return 'psaudio';
  return 'upnp';
}

/** Diagnostic rendererType — System Output never logs as psaudio (#1021). */
export function resolveLogRendererType(zone: {
  type?: string;
  name?: string;
  id?: string;
  metadata?: { transport_type?: unknown };
} | null | undefined): string {
  if (isLocalSystemOutputZone(zone)) {
    return zone?.type === 'local-native' ? 'local-native' : 'browser';
  }
  const raw = String(zone?.metadata?.transport_type || zone?.type || '');
  if (isSystemOutputLabel(zone?.name) && raw === 'psaudio') return 'browser';
  return raw || 'upnp';
}
